/**
 * FloatingClockWidget — the app-wide "pop out" clock.
 *
 * Vanessa, 29 Sep 2026: "i thought stop start clocks were pop outs, no?" --
 * TimeClockList.jsx (the /my-time page's own clocks section) is the real
 * implementation; this widget mounts once at the AppLayout shell (like
 * BonsaiMate/CaseMinderAssistant) so a running or paused clock stays
 * visible and controllable from any page, not just /my-time.
 *
 * Deliberately polls GET /time-clocks independently rather than sharing
 * state with TimeClockList -- the two mount in different places at
 * different times (this one is always mounted; TimeClockList only exists
 * while /my-time is). A few seconds of staleness between the collapsed
 * pill and the expanded list self-heals on the next poll tick, which is a
 * reasonable trade for not having to lift shared state through AppLayout.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Clock, Pause, Play, ChevronUp, Plus } from "lucide-react";
import { toast } from "sonner";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import TimeClockList from "./TimeClockList";

const POLL_MS = 20000;

function formatElapsed(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(sec).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

export default function FloatingClockWidget() {
  const [clocks, setClocks] = useState([]);
  const [fetchedAt, setFetchedAt] = useState(Date.now());
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [, forceTick] = useState(0);
  const pollRef = useRef(null);
  const tickRef = useRef(null);

  const poll = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/time-clocks`, { withCredentials: true });
      setClocks(resp.data?.clocks || []);
      setFetchedAt(Date.now());
    } catch {
      // Silent -- a failed background poll must never interrupt whatever
      // page the user is actually on. The next tick tries again.
    }
  }, []);

  useEffect(() => {
    poll();
    pollRef.current = setInterval(poll, POLL_MS);
    // BM-H4296: a clock started or changed elsewhere on the page (the matter
    // page Time card, My Time) shows here at once, not at the next poll.
    window.addEventListener("time-clocks-changed", poll);
    return () => {
      clearInterval(pollRef.current);
      window.removeEventListener("time-clocks-changed", poll);
    };
  }, [poll]);

  useEffect(() => {
    tickRef.current = setInterval(() => forceTick((t) => t + 1), 1000);
    return () => clearInterval(tickRef.current);
  }, []);

  const running = clocks.find((c) => c.status === "running");
  const primary = running || clocks[0] || null;
  const restCount = Math.max(0, clocks.length - (primary ? 1 : 0));

  const liveElapsed = (clock) => {
    if (!clock) return 0;
    if (clock.status !== "running") return clock.elapsed_seconds;
    return clock.elapsed_seconds + (Date.now() - fetchedAt) / 1000;
  };

  const handleCollapse = () => {
    setExpanded(false);
    poll();
  };

  const handleFinishedInList = () => {
    setExpanded(false);
    poll();
    // MyTime.jsx (if mounted) listens for this to refresh "My entries"
    // without this globally-mounted widget needing a direct reference to
    // that page's state.
    window.dispatchEvent(new CustomEvent("time-entry-logged"));
  };

  // BM-H4274 (Vanessa, 13 Oct 2026): "can we have a pause button on the time
  // clocks?". The pill could pause but not resume; both now work from it.
  const quickAction = async (e, action) => {
    e.stopPropagation();
    const wanted = action === "pause" ? "running" : "paused";
    if (busy || !primary || primary.status !== wanted) return;
    setBusy(true);
    try {
      const url = action === "pause"
        ? `${API}/time-clocks/${primary.id}/pause`
        : `${API}/time-clocks/${primary.id}/resume`;
      await axios.post(url, {}, { withCredentials: true });
      // Other clock lists on the page re-read too; this widget polls via the same event.
      window.dispatchEvent(new CustomEvent("time-clocks-changed"));
    } catch (err) {
      toast.error(errorMessage(err, `Couldn't ${action} that clock.`));
    }
    setBusy(false);
  };

  if (expanded) {
    return (
      <div
        className="fixed bottom-16 right-4 w-[400px] max-h-[70vh] bg-navy-hover border border-teal rounded-lg shadow-2xl z-40 flex flex-col overflow-hidden"
        data-testid="floating-clock-expanded"
      >
        <div className="flex items-center justify-between px-3 py-2 border-b border-navy-border shrink-0">
          <div className="flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 text-teal" />
            <span className="text-xs font-semibold text-white">Clocks</span>
          </div>
          <button
            type="button"
            onClick={handleCollapse}
            className="text-slate-500 hover:text-white p-1"
            title="Collapse"
            data-testid="floating-clock-collapse-btn"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
        </div>
        <div className="overflow-y-auto p-3">
          <TimeClockList onFinished={handleFinishedInList} />
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setExpanded(true)}
      className="fixed bottom-16 right-4 z-40 flex items-center gap-2 pl-3 pr-2 py-2 rounded-full bg-navy-hover border border-teal shadow-[0_4px_20px_rgba(0,0,0,0.6)] transition-colors"
      data-testid="floating-clock-pill"
    >
      {primary ? (
        <>
          <span
            className={`w-2 h-2 rounded-full ${primary.status === "running" ? "bg-teal animate-pulse" : "bg-amber-400"}`}
          />
          <span className="font-mono text-xs text-white">{primary.matter_ref}</span>
          <span className="font-mono text-xs text-slate-300">{formatElapsed(liveElapsed(primary))}</span>
          {primary.status !== "running" && <span className="text-[10px] text-amber-400">Paused</span>}
          {restCount > 0 && <span className="text-[10px] text-slate-500">+{restCount}</span>}
          {primary.status === "running" ? (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => quickAction(e, "pause")}
              className="text-slate-400 hover:text-amber-400 p-0.5 ml-0.5"
              title="Pause"
              data-testid="floating-clock-quick-pause"
            >
              <Pause className={`w-3.5 h-3.5 ${busy ? "opacity-50" : ""}`} />
            </span>
          ) : (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => quickAction(e, "resume")}
              className="text-slate-400 hover:text-white p-0.5 ml-0.5"
              title="Resume"
              data-testid="floating-clock-quick-resume"
            >
              <Play className={`w-3.5 h-3.5 ${busy ? "opacity-50" : ""}`} />
            </span>
          )}
        </>
      ) : (
        <>
          <Plus className="w-3.5 h-3.5 text-teal" />
          <span className="text-xs text-slate-300">Start a clock</span>
        </>
      )}
    </button>
  );
}
